/**
 * Unsaved drafts that survive a LAYOUT SWAP (#897).
 *
 * The app renders two different shells — `RootShell`'s `isPhone ? <MobileShell/>
 * : <AppLayout/>` at 767px, and the chat home's narrow/wide trees at 950px — so
 * crossing a breakpoint unmounts every screen and mounts a new one. A draft held
 * in component state goes with it. `useDraftState` keeps the draft HERE instead,
 * above both shells, so the screen the new shell mounts picks it back up.
 *
 * Plan and edge cases: docs/plans/2026-10-05_DRAFTS_SURVIVE_SHELL_SWAP_897.md.
 *
 * Rules:
 *  - A draft exists only once something was changed (`hasDraft`); until then the
 *    hook returns its LIVE fallback, so a screen that reloads its value from the
 *    server shows the new value — and stops doing so the moment the user edits.
 *    `clear()` on Save / Discard / Cancel returns to the fallback.
 *  - A draft is kept across an unmount ONLY when that unmount happened inside a
 *    swap window (`beginLayoutSwap`, opened by the breakpoint hooks the moment
 *    the media query flips, closed on the next task). Any other unmount — leaving
 *    the page, closing a dialog — drops it, as component state always did; the
 *    mobile shell remounts every page on navigation, so keeping drafts on every
 *    unmount would bring old drafts back.
 *  - The drop is decided a tick after the unmount and only if nothing re-mounted
 *    the key meanwhile: StrictMode's dev unmount → remount must not wipe a draft.
 *  - A kept draft is picked up when its screen next mounts — late mounts
 *    included (a lazy screen, the mobile workspace reopening tabs after a fetch)
 *    — within `DRAFT_CLAIM_WINDOW_MS` of the swap; one nobody claims by then is
 *    dropped, so it can never resurface, stale, on a later ordinary open.
 *  - `clearAllDrafts()` on a tenant switch and on logout: a draft never reaches
 *    another org or account. In memory only — a page reload loses drafts as before.
 *  - Keys name the item and its place (`db:<place>:<file>:<version>`,
 *    `budget:<tenant>:caps`), never a per-mount id. `key: null` = plain local
 *    state (no store): hosts that can't name their draft, the share view, tests.
 *
 * A module store (no provider) so it also works where no provider is mounted.
 */
import { createContext, createElement, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'

const drafts = new Map<string, unknown>()
/** Mounted users of a key — a key is only dropped when none are left. */
const holders = new Map<string, number>()
/** Bumped on every mount / unmount of a key: a pending drop acts only if it is
 *  still the LAST thing that happened to the key (no stale timer may delete a
 *  draft that was kept or re-claimed since). */
const activity = new Map<string, number>()
/** One counter for every key, never reset: each stamp is unique. */
let stamp = 0
function touch(key: string): number {
  stamp += 1
  activity.set(key, stamp)
  return stamp
}
const listeners = new Set<() => void>()
let swapOpen = false

function emit(): void {
  for (const l of listeners) l()
}
function subscribe(l: () => void): () => void {
  listeners.add(l)
  return () => { listeners.delete(l) }
}

/**
 * A breakpoint is about to swap the shell: unmounts until the next task keep
 * their drafts. Idempotent (every subscriber of the media query may call it).
 * Called from the media-query change handler, before React re-renders; the
 * swap's render + commit run before the next task, so a 0ms timer closes it.
 */
export function beginLayoutSwap(): void {
  if (swapOpen) return
  swapOpen = true
  // Snapshot hooks first, BEFORE React renders the new tree: a screen whose
  // draft lives in refs (the DB grid) stashes it now, so the new screen's
  // first render finds it whatever order the commit then runs in.
  for (const cb of swapStartListeners) {
    try { cb() } catch { /* one screen's snapshot must not stop the others */ }
  }
  setTimeout(() => {
    swapOpen = false
    // Screens that are STILL mounted when the window closes were not swapped
    // (e.g. the chat home's 950px swap leaves the workspace alone): they drop
    // whatever they snapshotted at swap start, so it can't resurface later.
    for (const cb of swapEndListeners) {
      try { cb() } catch { /* as above */ }
    }
    scheduleClaimSweep()
  }, 0)
}

const swapEndListeners = new Set<() => void>()
/** Run `cb` when the swap window closes, if still subscribed (still mounted). Returns an unsubscribe. */
export function onLayoutSwapEnd(cb: () => void): () => void {
  swapEndListeners.add(cb)
  return () => { swapEndListeners.delete(cb) }
}

/**
 * How long a draft a swap kept may wait for its screen to mount again. Long
 * enough for the late mounts (the phone workspace reopening tabs after a
 * config fetch, the DB grid after its schema fetch); short enough that a
 * draft nobody claims — its screen does not exist on the other shell (a
 * desktop-only dialog), or its host does not carry the dialog over — never
 * resurfaces on a later, ordinary open with stale content.
 */
export const DRAFT_CLAIM_WINDOW_MS = 15_000
let sweepTimer: ReturnType<typeof setTimeout> | null = null
function scheduleClaimSweep(): void {
  if (sweepTimer) clearTimeout(sweepTimer)
  sweepTimer = setTimeout(() => {
    sweepTimer = null
    let changed = false
    for (const key of [...drafts.keys()]) {
      if ((holders.get(key) ?? 0) > 0) continue // claimed — a mounted screen holds it
      drafts.delete(key)
      changed = true
    }
    stashes.clear()
    if (changed) emit()
  }, DRAFT_CLAIM_WINDOW_MS)
}

const swapStartListeners = new Set<() => void>()
/** Run `cb` the moment a layout swap starts (before the new tree renders). Returns an unsubscribe. */
export function onLayoutSwapStart(cb: () => void): () => void {
  swapStartListeners.add(cb)
  return () => { swapStartListeners.delete(cb) }
}

/**
 * Every draft, gone — a tenant switch or logout. `silent` skips notifying the
 * mounted hooks (call it during a render, where a notify would update other
 * components mid-render; their next store read sees the empty store anyway),
 * and is followed by a call with `sameReset` from an effect — it notifies (and
 * drops anything written in between) as part of the SAME reset, so a request
 * started between the two is not treated as one from before it.
 *
 * A request still running belongs to the account / org that started it: its
 * outcome is not handed over to whoever is signed in when it lands (it would
 * carry e.g. a new share link's URL to the next user). Its in-flight flag is
 * left to the request itself — a screen that outlives the reset (the billing
 * deck stays mounted across a tenant switch) must not see its own create go
 * idle mid-flight and offer a second one.
 */
export function clearAllDrafts(opts?: { silent?: boolean; sameReset?: boolean }): void {
  if (!opts?.sameReset) resetGeneration += 1
  stashes.clear()
  pendingOutcomes.clear()
  drafts.clear()
  // Always notify when not silent: after a silent clear the store is already
  // empty, and the mounted hooks still need telling.
  if (!opts?.silent) emit()
}

// ── Swap stashes ─────────────────────────────────────────────────────────
// For a screen whose draft lives in refs it writes on every keystroke (the
// database grid's typed cells), where routing each write through
// `useDraftState` would touch every edit path. It SNAPSHOTS its draft when it
// unmounts inside a swap window, and the screen the new shell mounts reads it
// while building its first render (`peekSwapStash` — non-destructive, so
// StrictMode's double-called initializers both see it) and claims it after
// mount (`claimSwapStash`). Same lifetime rules as drafts: only a swap stashes,
// a tenant switch or logout clears.

const stashes = new Map<string, unknown>()

/** Whether a layout swap is unmounting the current tree right now. */
export function isLayoutSwapOpen(): boolean {
  return swapOpen
}

/** Snapshot `value` under `key` — only inside a swap window; returns whether it did. */
export function stashForSwap(key: string, value: unknown): boolean {
  if (!swapOpen) return false
  stashes.set(key, value)
  return true
}

/** The stash under `key`, left in place (read it in a state initializer). */
export function peekSwapStash<T>(key: string): T | undefined {
  return stashes.get(key) as T | undefined
}

/** Take the stash under `key` out — after the screen that read it mounted. */
export function claimSwapStash(key: string): void {
  stashes.delete(key)
}

/** Whether a draft is stored under `key` (for code outside the screen, e.g. a save that answers after it unmounted). */
export function hasDraft(key: string): boolean {
  return drafts.has(key)
}

/** Drop the draft under `key` — a save that succeeded after its screen unmounted. */
export function clearDraft(key: string): void {
  if (drafts.delete(key)) emit()
}

function hold(key: string): () => void {
  holders.set(key, (holders.get(key) ?? 0) + 1)
  touch(key)
  return () => {
    holders.set(key, Math.max(0, (holders.get(key) ?? 1) - 1))
    const mine = touch(key)
    // Decided NOW: was this unmount part of a layout swap?
    if (swapOpen) return
    setTimeout(() => {
      if (activity.get(key) !== mine) return // re-mounted or re-released since
      if ((holders.get(key) ?? 0) > 0) return
      holders.delete(key)
      activity.delete(key)
      if (drafts.delete(key)) emit()
    }, 0)
  }
}

export interface DraftControls<T = unknown> {
  /** Something was changed and not yet saved or discarded. */
  hasDraft: boolean
  /** Back to the fallback NOW — Discard, Cancel, or a Save whose reload already landed. */
  clear: () => void
  /**
   * A Save went through, but the fallback (the server value) has not caught up
   * yet: keep showing the draft until it CHANGES (the reload landed) or already
   * equals the draft, then drop it. `clear()` here would flash the old value
   * until the reload — and keep showing it, unmarked, if the reload failed.
   * A new edit cancels it. Compared by JSON, so for plain-data drafts only.
   *
   * `sent` — the value as the SERVER will store it, in the draft's shape, when
   * that can differ from what was typed (normalised e-mails, an empty list sent
   * as "no restriction"): the draft also drops once the fallback equals it,
   * which it may ALREADY do (a save that stored the value unchanged).
   */
  settle: (sent?: T) => void
}

/**
 * `useState` whose value survives a layout swap. `fallback` is LIVE: it is what
 * the hook returns while there is no draft (pass the server value, and the
 * screen follows reloads until the user edits). A function fallback is called
 * each render while there is no draft.
 */
export function useDraftState<T>(
  key: string | null,
  fallback: T | (() => T),
  opts?: {
    /**
     * When a value counts as the fallback (no edit). Default: same plain data.
     * Pass the screen's OWN "changed?" rule when it is looser — a set, a
     * normalised value — or a value the screen shows as unchanged stays a
     * hidden draft that pins the old value over every later reload, and the
     * next Save writes it back over someone else's change.
     *
     * NEVER a normalising rule (trim, parse) on a field written per KEYSTROKE:
     * "Sales " over "Sales", or "5." over "5", would count as unchanged and the
     * character being typed would vanish. Those fields keep the default and
     * rely on `settle`.
     */
    isSame?: (a: T, b: T) => boolean
  },
): [T, (next: T | ((prev: T) => T)) => void, DraftControls<T>] {
  const resolve = (): T => (typeof fallback === 'function' ? (fallback as () => T)() : fallback)

  // `key: null` — plain local state, same shape.
  const [local, setLocal] = useState<{ value: T } | null>(null)

  /** This screen is mounted. A write from one that is gone — a request that
   *  answered after a layout swap replaced it — is refused even when the new
   *  screen holds the key: it computes from the old screen's state, and would
   *  overwrite what the user did since on the new one. The new screen hears
   *  how the request ended through `useDraftInFlight`'s `onSettled`. */
  const mountedRef = useRef(false)

  // The stored value itself (stable — only `set` replaces it), or NONE.
  const stored = useSyncExternalStore(
    subscribe,
    () => (key !== null && drafts.has(key) ? drafts.get(key) : NONE),
    () => NONE,
  )

  // A LAYOUT effect: it runs before any passive effect of the screen, so a
  // mount-time write from the screen's own effects always finds the key held
  // (`set` refuses writes to an unheld key outside a swap).
  useLayoutEffect(() => {
    mountedRef.current = true
    const release = key === null ? undefined : hold(key)
    return () => { mountedRef.current = false; release?.() }
  }, [key])

  const current: { value: T } | null = key === null
    ? local
    : stored === NONE ? null : { value: stored as T }
  const value = current ? current.value : resolve()


  const settleRef = useRef<{ fallback: string; sent?: string } | null>(null)
  /** The CURRENT render's fallback, for `settle` (a stable callback must not
   *  read the fallback of the render that created it). */
  const isSameRef = useRef(opts?.isSame)
  isSameRef.current = opts?.isSame
  const fallbackRef = useRef(fallback)
  fallbackRef.current = fallback

  const set = useCallback((next: T | ((prev: T) => T)) => {
    settleRef.current = null // a new edit is not part of the settling Save
    // A value equal to the CURRENT fallback is no edit (typed back, toggled
    // back): there is no draft, and the fallback stays live. Kept, it pinned
    // that value — a later reload never showed (keep-alive decks never
    // remount) and Save wrote it over someone else's change.
    const noEdit = (v: T) => (isSameRef.current ?? sameJson)(v, currentFallback())
    if (key === null) {
      setLocal(prev => {
        // No draft: build on the fallback NOW — the last render's value may
        // hold a draft an earlier update in this same event just dropped.
        const base = prev ? prev.value : currentFallback()
        const v = typeof next === 'function' ? (next as (p: T) => T)(base) : next
        return noEdit(v) ? null : { value: v }
      })
      return
    }
    // No screen holds this key (it unmounted) and no swap is keeping it: a
    // write that lands now — a Save answering after the user left the page —
    // would leave a draft nobody drops, back on the next ordinary visit.
    if ((holders.get(key) ?? 0) === 0 && !swapOpen) return
    if (!mountedRef.current) return
    const base = drafts.has(key) ? (drafts.get(key) as T) : currentFallback()
    const v = typeof next === 'function' ? (next as (p: T) => T)(base) : next
    if (noEdit(v)) {
      if (drafts.delete(key)) emit()
      return
    }
    drafts.set(key, v)
    emit()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  /** The stored value this screen last rendered (for a late `clear`). */
  const seenStoredRef = useRef(stored)
  seenStoredRef.current = stored
  const clear = useCallback(() => {
    if (key === null) { setLocal(null); return }
    // From a screen that is gone (a Save answering after a swap replaced it):
    // only if the draft is still the one it last saw. Once the new screen
    // edited it, it is that screen's work — a late success must not wipe it.
    if (!mountedRef.current && (drafts.has(key) ? drafts.get(key) : NONE) !== seenStoredRef.current) return
    clearDraft(key)
  }, [key])

  const currentFallback = (): T => {
    const fb = fallbackRef.current
    return typeof fb === 'function' ? (fb as () => T)() : fb
  }
  const settle = useCallback((sent?: T) => {
    settleRef.current = { fallback: json(currentFallback()), ...(sent !== undefined ? { sent: json(sent) } : {}) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  // Every render: drop a settling draft once the fallback moved, or matches it.
  useEffect(() => {
    const st = settleRef.current
    if (!st) return
    if (current === null) { settleRef.current = null; return }
    const now = json(currentFallback())
    if (now !== st.fallback || now === json(current.value) || (st.sent !== undefined && now === st.sent)) {
      settleRef.current = null
      clear()
    }
  })

  return [value, set, { hasDraft: current !== null, clear, settle }]
}

const NONE: unknown = Object.freeze({ none: true })

/** Plain-data comparison for drafts (JSON; a value that can't serialise
 *  compares unequal to anything but itself). Sets and Maps are written out —
 *  plain JSON turns every one into `{}`, so any two would compare equal. */
function json(v: unknown): string {
  try {
    return JSON.stringify(v, (_k, x: unknown) =>
      x instanceof Set ? { $set: [...x] } : x instanceof Map ? { $map: [...x] } : x) ?? ''
  } catch { return '' }
}
function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true
  const ja = json(a)
  return ja !== '' && ja === json(b)
}

// ── Submits in flight ────────────────────────────────────────────────────
// A draft that survives a swap brings its form back filled in — but a
// "submitting" flag in component state does not come with it. A swap during a
// create / record request would show the restored form with its submit button
// LIVE while the first request is still running (from the unmounted screen),
// and a second click writes the record twice (a duplicate plan, a second
// ledger row, a second link + its e-mails). The flag lives here, next to the
// draft: `track(work)` marks the key in flight until `work` settles, whichever
// screen is mounted by then.
//
// And the request's END must reach that screen: the old screen's success path
// resets ITS form (refused now — it is gone) and reloads ITS list (unmounted),
// so the new screen would keep the filled form, live again, and a list without
// the record just made. When a request settles after the screen that started
// it unmounted, its outcome goes to `onSettled` of every screen now holding the
// key — or, if none does yet (a late mount), to the next one that mounts within
// `DRAFT_CLAIM_WINDOW_MS`. The new screen resets its form and reloads there.
// A request whose own screen is still mounted handles its end as it always did;
// one whose screen was closed (no swap) hands nothing over.

/** How a tracked request ended, for a screen that did not start it. */
export type DraftOutcome<V = unknown> = { ok: true; value: V } | { ok: false }

const inFlight = new Map<string, number>()
/** Bumped by `clearAllDrafts` (tenant switch, logout): a request started
 *  before it hands nothing over. */
let resetGeneration = 0
/** Bumped only by `__resetDraftStoreForTests`, which empties `inFlight`. */
let testResetEpoch = 0
type SettledRef = { current: ((o: DraftOutcome) => void) | undefined }
const settledHandlers = new Map<string, Map<object, SettledRef>>()
const pendingOutcomes = new Map<string, { outcome: DraftOutcome; at: number }>()

/** Screens a layout swap unmounted (their requests' outcomes are passed on). */
const swappedAway = new WeakSet<object>()

/** Requests are numbered; a screen with an `onSettled` that the user CLOSED
 *  (no swap) cuts every request for its key started up to then off from the
 *  hand-over — its form went with it, and a later screen is a new form. */
let requestSeq = 0
const closedUpTo = new Map<string, number>()

function deliverOutcome(key: string, starter: object, outcome: DraftOutcome, seq: number): void {
  if (seq <= (closedUpTo.get(key) ?? 0)) return
  const handlers = settledHandlers.get(key)
  if (handlers?.has(starter)) return // its own screen is still there
  // Only a SWAP hands a request over — like a draft. A screen the user closed
  // (a dialog dismissed, a page left) took its form with it; a later screen
  // for the same key is a new form, and must not be reset by an old request.
  if (!swappedAway.has(starter)) return
  if (handlers && handlers.size > 0) {
    for (const h of [...handlers.values()]) h.current?.(outcome)
    return
  }
  pendingOutcomes.set(key, { outcome, at: Date.now() })
}

/**
 * `[busy, track]` for a submit that writes what the draft `key` holds. `busy`
 * is true while any `track`ed request for the key runs — started by this
 * screen or by the one a swap replaced. `onSettled` hears how a request
 * started by ANOTHER screen ended (see above); `value` is what `work`
 * resolved with. `key: null` = local only.
 */
export function useDraftInFlight<V = unknown>(
  key: string | null,
  onSettled?: (outcome: DraftOutcome<V>) => void,
): [boolean, <R extends V>(work: () => Promise<R>) => Promise<R>] {
  const [local, setLocal] = useState(0)
  const [self] = useState<object>(() => ({}))
  const handlerRef = useRef(onSettled as SettledRef['current'])
  handlerRef.current = onSettled as SettledRef['current']
  const shared = useSyncExternalStore(
    subscribe,
    () => (key === null ? 0 : inFlight.get(key) ?? 0),
    () => 0,
  )
  useLayoutEffect(() => {
    if (key === null) return
    let handlers = settledHandlers.get(key)
    if (!handlers) { handlers = new Map(); settledHandlers.set(key, handlers) }
    handlers.set(self, handlerRef)
    const mountedAtSeq = requestSeq
    // Mounted again (StrictMode's dev re-setup runs inside a swap window too):
    // not swapped away any more.
    swappedAway.delete(self)
    return () => {
      if (swapOpen) swappedAway.add(self)
      const hs = settledHandlers.get(key)
      hs?.delete(self)
      if (hs && hs.size === 0) settledHandlers.delete(key)
      // Closed by the user (not a swap): decided a tick later, so StrictMode's
      // dev unmount → remount (same screen, registered again) is not a close.
      if (!swapOpen && handlerRef.current) {
        // Only requests started BEFORE this screen mounted — the ones it can
        // have been restored for. Closing a screen that was already there
        // when a later request started does not cut that request: its
        // restored screen may simply mount late.
        const upTo = mountedAtSeq
        setTimeout(() => {
          // Re-registered (StrictMode), or another screen still holds the key
          // (keep-alive decks): that one's requests are still its own.
          if ((settledHandlers.get(key)?.size ?? 0) > 0) return
          closedUpTo.set(key, Math.max(closedUpTo.get(key) ?? 0, upTo))
        }, 0)
      }
    }
  }, [key, self])
  // A request that settled while no screen held the key is handed over in a
  // PASSIVE effect: by then every layout effect of this commit ran, so the
  // screen's own `useDraftState` keys are held and mounted — a handler that
  // resets the form with `set` would otherwise be refused.
  useEffect(() => {
    // A screen that listens for nothing leaves it for one that does (the
    // plan form registers the key to read `busy`; its host handles the end).
    if (key === null || !handlerRef.current) return
    const pending = pendingOutcomes.get(key)
    if (!pending) return
    pendingOutcomes.delete(key)
    if (Date.now() - pending.at <= DRAFT_CLAIM_WINDOW_MS) handlerRef.current?.(pending.outcome)
  }, [key])
  const track = useCallback(async <R extends V>(work: () => Promise<R>): Promise<R> => {
    const generation = resetGeneration
    const testEpoch = testResetEpoch
    const seq = ++requestSeq
    const bump = (d: number) => {
      if (key === null) { setLocal(n => n + d); return }
      if (testEpoch !== testResetEpoch) return // tests only: a request a previous test left running
      const n = (inFlight.get(key) ?? 0) + d
      if (n > 0) inFlight.set(key, n)
      else inFlight.delete(key)
      emit()
    }
    bump(1)
    let outcome: DraftOutcome = { ok: false }
    try {
      const value = await work()
      outcome = { ok: true, value }
      return value
    } finally {
      bump(-1)
      if (key !== null && generation === resetGeneration) deliverOutcome(key, self, outcome, seq)
    }
  }, [key, self])
  return [(key === null ? local : shared) > 0, track]
}

/** Whether a `track`ed submit for `key` is running (for event handlers). */
export function isDraftInFlight(key: string): boolean {
  return (inFlight.get(key) ?? 0) > 0
}

// ── Draft scope ──────────────────────────────────────────────────────────
// WHERE an editor is open — `ws:<workspace>`, `single`, `chat` — provided by the
// host around its editors, so a draft key names the place as well as the file:
// the same file open in two desktop workspaces must not share one draft, while
// the phone workspace (same workspace id, same URL) must find the desktop one's.
// No provider = no scope = drafts not kept (the share view, previews).

const DraftScopeContext = createContext<string | null>(null)

export function DraftScopeProvider({ scope, children }: { scope: string | null; children?: ReactNode }) {
  return createElement(DraftScopeContext.Provider, { value: scope }, children)
}

/** The host's draft scope, or null. */
export function useDraftScope(): string | null {
  return useContext(DraftScopeContext)
}

/** Test-only: reset the module store between tests. */
export function __resetDraftStoreForTests(): void {
  drafts.clear()
  stashes.clear()
  inFlight.clear()
  closedUpTo.clear()
  resetGeneration += 1 // a request a previous test left running hands nothing to the next
  testResetEpoch += 1 // …and does not decrement the next test's count
  settledHandlers.clear()
  pendingOutcomes.clear()
  holders.clear()
  activity.clear()
  swapStartListeners.clear()
  swapEndListeners.clear()
  if (sweepTimer) { clearTimeout(sweepTimer); sweepTimer = null }
  swapOpen = false
  emit()
}
